"use client"

import { useCallback, useMemo, useState, type ComponentProps } from "react"
import { Link } from "react-router-dom"
import {
  ChevronDown,
  ChevronRight,
  Pin,
  MoreHorizontal,
  FolderInput,
  Share2,
  Archive,
  Trash2,
  GripVertical,
  FileText,
} from "lucide-react"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
  MenuSeparator,
} from "@/components/ui/menu"
import { cn } from "src/utils/Functions"
import { QuickAccessItem } from "@/components/sidebar/quick-access-prefs"

type NavQuickAccessProps = {
  items: QuickAccessItem[]
  onReorder?: (items: QuickAccessItem[]) => void
  onTogglePin?: (itemId: string) => void
  onMoveToFolder?: (itemId: string) => void
  onShare?: (itemId: string) => void
  onArchive?: (itemId: string) => void
  onDelete?: (itemId: string) => void
}

type SortableQuickAccessItemProps = {
  item: QuickAccessItem
  onTogglePin?: (itemId: string) => void
  onMoveToFolder?: (itemId: string) => void
  onShare?: (itemId: string) => void
  onArchive?: (itemId: string) => void
  onDelete?: (itemId: string) => void
}

function SortableQuickAccessItem({
  item,
  onTogglePin,
  onMoveToFolder,
  onShare,
  onArchive,
  onDelete,
}: SortableQuickAccessItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: item.id,
    })

  const style = useMemo(
    () => ({
      transform: CSS.Translate.toString(transform),
      transition,
    }),
    [transform, transition],
  )

  return (
    <SidebarMenuItem>
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "relative rounded-md transition-opacity has-data-popup-open:bg-sidebar-accent",
          isDragging && "opacity-60",
        )}
      >
        <SidebarMenuButton asChild tooltip={item.name}>
          <div className="group/item flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <button
                type="button"
                aria-label={`Reorder ${item.name}`}
                className="flex size-5 cursor-grab items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
                onClick={(event) => event.preventDefault()}
                {...attributes}
                {...listeners}
              >
                <span className="relative flex size-3.5 items-center justify-center">
                  <FileText
                    className={cn(
                      "absolute size-3.5 transition-all duration-150",
                      isDragging
                        ? "scale-75 opacity-0"
                        : "scale-100 opacity-100 group-hover/item:scale-75 group-hover/item:opacity-0 group-focus-within/item:scale-75 group-focus-within/item:opacity-0",
                    )}
                  />
                  <GripVertical
                    className={cn(
                      "absolute size-3.5 transition-all duration-150",
                      isDragging
                        ? "scale-100 opacity-100"
                        : "scale-75 opacity-0 group-hover/item:scale-100 group-hover/item:opacity-100 group-focus-within/item:scale-100 group-focus-within/item:opacity-100",
                    )}
                  />
                </span>
              </button>
              <Link to={item.url} className="min-w-0 flex-1">
                <span className="block truncate">{item.name}</span>
              </Link>
            </div>

            <div className="flex shrink-0 items-center gap-0.5">
              {item.isPinned ? (
                <PinIcon
                  isPinned={item.isPinned}
                  className="size-3 rotate-45 text-muted-foreground"
                />
              ) : null}
              <Menu>
                <MenuTrigger
                  className={cn(
                    "rounded-sm p-1 text-muted-foreground transition-all hover:bg-white/10 hover:text-foreground data-popup-open:bg-muted",
                    "not-data-popup-open:opacity-0 group-hover/item:opacity-100",
                  )}
                >
                  <MoreHorizontal className="size-3.5" />
                </MenuTrigger>
                <MenuPopup side="right" align="start" sideOffset={-4} className="w-48">
                  <MenuItem onClick={() => onMoveToFolder?.(item.id)}>
                    <FolderInput className="size-4" />
                    Move to folder…
                  </MenuItem>
                  <MenuItem onClick={() => onShare?.(item.id)}>
                    <Share2 className="size-4" />
                    Share…
                  </MenuItem>
                  <MenuItem onClick={() => onArchive?.(item.id)}>
                    <Archive className="size-4" />
                    Archive
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem onClick={() => onTogglePin?.(item.id)}>
                    <Pin className={cn("size-4", item.isPinned && "fill-current")} />
                    {item.isPinned ? "Unpin" : "Pin"}
                  </MenuItem>
                  <MenuItem variant="destructive" onClick={() => onDelete?.(item.id)}>
                    <Trash2 className="size-4" />
                    Delete
                  </MenuItem>
                </MenuPopup>
              </Menu>
            </div>
          </div>
        </SidebarMenuButton>
      </div>
    </SidebarMenuItem>
  )
}

export function NavQuickAccess({
  items,
  onReorder,
  onTogglePin,
  onMoveToFolder,
  onShare,
  onArchive,
  onDelete,
}: NavQuickAccessProps) {
  const [isOpen, setIsOpen] = useState(true)

  const pinnedItems = useMemo(
    () => items.filter((item) => Boolean(item.isPinned)),
    [items],
  )
  const unpinnedItems = useMemo(
    () => items.filter((item) => !item.isPinned),
    [items],
  )
  const pinnedItemIds = useMemo(
    () => pinnedItems.map((item) => item.id),
    [pinnedItems],
  )
  const unpinnedItemIds = useMemo(
    () => unpinnedItems.map((item) => item.id),
    [unpinnedItems],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!onReorder || !over || active.id === over.id) {
        return
      }

      const activeId = String(active.id)
      const overId = String(over.id)
      const activeItem = items.find((item) => item.id === activeId)
      const overItem = items.find((item) => item.id === overId)

      if (!activeItem || !overItem) {
        return
      }

      const isPinnedMove = Boolean(activeItem.isPinned)
      if (isPinnedMove !== Boolean(overItem.isPinned)) {
        return
      }

      const sourceItems = isPinnedMove ? pinnedItems : unpinnedItems
      const fromIndex = sourceItems.findIndex((item) => item.id === activeId)
      const toIndex = sourceItems.findIndex((item) => item.id === overId)

      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return
      }

      const reorderedSource = arrayMove(sourceItems, fromIndex, toIndex)
      const nextItems = isPinnedMove
        ? [...reorderedSource, ...unpinnedItems]
        : [...pinnedItems, ...reorderedSource]

      onReorder(nextItems)
    },
    [items, onReorder, pinnedItems, unpinnedItems],
  )

  if (items.length === 0) {
    return null
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <CollapsibleTrigger className="w-full">
          <SidebarGroupLabel className="flex items-center justify-between cursor-pointer group">
            <span>Quick Access</span>
            <div className="opacity-60 group-hover:opacity-100 transition-opacity">
              {isOpen ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </div>
          </SidebarGroupLabel>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SidebarMenu>
              {pinnedItems.length > 0 ? (
                <SortableContext
                  items={pinnedItemIds}
                  strategy={verticalListSortingStrategy}
                >
                  {pinnedItems.map((item) => (
                    <SortableQuickAccessItem
                      key={item.id}
                      item={item}
                      onTogglePin={onTogglePin}
                      onMoveToFolder={onMoveToFolder}
                      onShare={onShare}
                      onArchive={onArchive}
                      onDelete={onDelete}
                    />
                  ))}
                </SortableContext>
              ) : null}
              {pinnedItems.length > 0 && unpinnedItems.length > 0 ? (
                <SidebarMenuItem className="pointer-events-none">
                  <div className="mx-2 my-1 h-px bg-sidebar-border/60" />
                </SidebarMenuItem>
              ) : null}
              {unpinnedItems.length > 0 ? (
                <SortableContext
                  items={unpinnedItemIds}
                  strategy={verticalListSortingStrategy}
                >
                  {unpinnedItems.map((item) => (
                    <SortableQuickAccessItem
                      key={item.id}
                      item={item}
                      onTogglePin={onTogglePin}
                      onMoveToFolder={onMoveToFolder}
                      onShare={onShare}
                      onArchive={onArchive}
                      onDelete={onDelete}
                    />
                  ))}
                </SortableContext>
              ) : null}
            </SidebarMenu>
          </DndContext>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  )
}

type PinIconProps = {
  isPinned: boolean
} & ComponentProps<"svg">

export function PinIcon({ isPinned, ...props }: PinIconProps) {
  return (
    <div className="">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M12 15.25H4.75V12.5L5.12653 12.1235C6.48616 10.7638 7.25 8.91977 7.25 6.99695V5.75C7.25 4.09315 8.59315 2.75 10.25 2.75H13.75C15.4069 2.75 16.75 4.09315 16.75 5.75V6.99695C16.75 8.91977 17.5138 10.7638 18.8735 12.1235L19.25 12.5V15.25H12ZM12 15.25V21.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  )
}
